import { useRef, useState } from "react";
import { Loader2, Upload, X } from "lucide-react";
import { uploadImage } from "@/lib/api";

const inputCls =
  "w-full rounded-lg border border-navy-border bg-navy px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-volt focus:outline-none";

export function Field({ label, value, onChange, textarea, testId }) {
  return (
    <div>
      <label className="mb-1.5 block font-mono text-xs uppercase tracking-widest text-slate-400">{label}</label>
      {textarea ? (
        <textarea data-testid={testId} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />
      ) : (
        <input data-testid={testId} value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />
      )}
    </div>
  );
}

export function BiField({ label, value, onChange, textarea, testId }) {
  const v = value || { es: "", en: "" };
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label={`${label} (ES)`} value={v.es} onChange={(es) => onChange({ ...v, es })} textarea={textarea} testId={testId ? `${testId}-es` : undefined} />
      <Field label={`${label} (EN)`} value={v.en} onChange={(en) => onChange({ ...v, en })} textarea={textarea} testId={testId ? `${testId}-en` : undefined} />
    </div>
  );
}

export function ImageField({ label, value, onChange, testId }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const url = await uploadImage(f);
      onChange(url);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid={testId}>
      <label className="mb-1.5 block font-mono text-xs uppercase tracking-widest text-slate-400">{label}</label>
      <div className="flex items-start gap-3">
        {value ? (
          <img src={value} alt="" className="h-20 w-28 shrink-0 rounded-lg border border-navy-border object-cover" />
        ) : (
          <div className="flex h-20 w-28 shrink-0 items-center justify-center rounded-lg border border-dashed border-navy-border text-slate-600">
            <Upload size={18} />
          </div>
        )}
        <div className="flex-1 space-y-2">
          <input
            data-testid={testId ? `${testId}-url` : undefined}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="URL de la imagen"
            className={inputCls}
          />
          <div className="flex gap-2">
            <button
              type="button"
              data-testid={testId ? `${testId}-upload` : undefined}
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="flex items-center gap-2 rounded-lg bg-navy-border px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-volt hover:text-navy disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Subir
            </button>
            {value && (
              <button
                type="button"
                data-testid={testId ? `${testId}-clear` : undefined}
                onClick={() => onChange("")}
                className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold text-red-400 hover:bg-red-500/10"
              >
                <X size={14} /> Quitar
              </button>
            )}
          </div>
        </div>
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={pick} />
    </div>
  );
}
